/**
 * risk.service.js — Risks / vulnerabilities domain service.
 * Open vulns list/detail/notes/status, exports, and AI top risks.
 */
import {
  toApiUrl,
  fetchWithAuthRetry,
  jsonFetchOptions,
  parseJsonOrThrow,
  readApiErrorMessage,
  handleUnauthorizedResponse,
  isLicenseGatedMessage,
} from './_core';

function severityLabelToFloat(raw) {
  const numeric = Number.parseFloat(raw);
  if (Number.isFinite(numeric)) return numeric;
  const label = String(raw || '').trim().toLowerCase();
  if (label === 'critical') return 9.5;
  if (label === 'high') return 7.5;
  if (label === 'medium') return 5.0;
  if (label === 'low' || label === 'log' || label === 'info' || label === 'informational') return 2.0;
  return null;
}

function impactValue(raw) {
  if (raw == null || String(raw).trim() === '') return null;
  const numeric = Number.parseFloat(raw);
  return Number.isFinite(numeric) ? numeric : null;
}

function aggregateTopOpenVulnerabilityRows(rows) {
  const grouped = new Map();
  for (const row of rows) {
    const title = String(row?.title || '').trim();
    const severity = String(row?.severity || '').trim();
    if (!title) continue;

    const severityFloat =
      severityLabelToFloat(row?.severity_float) ?? severityLabelToFloat(severity);
    const technicalImpact = impactValue(row?.technical_impact);
    const businessImpact = impactValue(row?.business_impact);
    const rowCount = Math.max(1, Number(row?.count) || 1);
    const key = `${title}::${severity || 'Unknown'}`;
    const current = grouped.get(key);

    if (!current) {
      grouped.set(key, {
        title,
        severity: severity || 'Unknown',
        severity_float: severityFloat,
        technical_impact: technicalImpact,
        business_impact: businessImpact,
        count: rowCount,
      });
      continue;
    }

    current.count += rowCount;
    if (current.severity_float == null && severityFloat != null) {
      current.severity_float = severityFloat;
    }
    if (technicalImpact != null) {
      current.technical_impact = Math.max(current.technical_impact ?? technicalImpact, technicalImpact);
    }
    if (businessImpact != null) {
      current.business_impact = Math.max(current.business_impact ?? businessImpact, businessImpact);
    }
  }

  return Array.from(grouped.values()).sort(
    (a, b) => (Number(b.severity_float) || -1) - (Number(a.severity_float) || -1)
  );
}

function extractTopOpenRows(data) {
  if (Array.isArray(data)) return data;
  if (!data || typeof data !== 'object') return [];
  if (Array.isArray(data.results)) return data.results;
  return [
    ...(Array.isArray(data.network) ? data.network : []),
    ...(Array.isArray(data.web) ? data.web : []),
    ...(Array.isArray(data.vulnerabilities) ? data.vulnerabilities : []),
  ];
}

export async function getTopOpenVulnerabilities(token = null, query = null) {
  const qs =
    query && typeof query === 'object' && Object.keys(query).length > 0
      ? `?${new URLSearchParams({ ...query, limit: query.limit || 10 }).toString()}`
      : '?limit=10';
  const response = await fetchWithAuthRetry(
    toApiUrl(`/dashboard/vulnerabilities/top-open/${qs}`),
    jsonFetchOptions(token),
    token
  );
  if (!response.ok) {
    const msg = await readApiErrorMessage(response, `Vulnerabilities list failed (${response.status})`);
    if (isLicenseGatedMessage(msg)) return [];
    throw new Error(msg);
  }
  const data = await parseJsonOrThrow(response, 'Open vulnerabilities');
  return aggregateTopOpenVulnerabilityRows(extractTopOpenRows(data));
}

export async function getDashboardVulnerabilities(query = {}, token = null) {
  const params = new URLSearchParams();
  if (query && typeof query === 'object') {
    Object.entries(query).forEach(([key, value]) => {
      const normalized = String(value ?? '').trim();
      if (normalized) params.set(key, normalized);
    });
  }

  const qs = params.toString() ? `?${params.toString()}` : '';
  const response = await fetchWithAuthRetry(toApiUrl(`/dashboard/vulnerabilities/${qs}`), jsonFetchOptions(token), token);
  if (!response.ok) {
    if (response.status === 401) {
      await handleUnauthorizedResponse(response);
    }
    const msg = await readApiErrorMessage(response, `Vulnerabilities list failed (${response.status})`);
    if (isLicenseGatedMessage(msg)) return { network: [], web: [] };
    throw new Error(msg);
  }

  const data = await parseJsonOrThrow(response, 'Vulnerabilities list');
  return {
    network: Array.isArray(data?.network) ? data.network : [],
    web: Array.isArray(data?.web) ? data.web : [],
  };
}

export async function getDashboardVulnerabilityDetail(vulnId, query = {}, token = null) {
  const id = String(vulnId || '').trim();
  if (!id) throw new Error('vuln_id is required.');
  const params = new URLSearchParams();
  if (query && typeof query === 'object') {
    Object.entries(query).forEach(([key, value]) => {
      const normalized = String(value ?? '').trim();
      if (normalized) params.set(key, normalized);
    });
  }
  const qs = params.toString() ? `?${params.toString()}` : '';
  const response = await fetchWithAuthRetry(toApiUrl(`/dashboard/vulnerabilities/${encodeURIComponent(id)}/${qs}`), jsonFetchOptions(token), token);
  if (!response.ok) {
    if (response.status === 401) {
      await handleUnauthorizedResponse(response);
    }
    const msg = await readApiErrorMessage(response, `Vulnerability detail failed (${response.status})`);
    throw new Error(msg);
  }
  return parseJsonOrThrow(response, 'Vulnerability detail');
}

export async function postDashboardVulnerabilityNote(vulnId, payload = {}, token = null) {
  const id = String(vulnId || '').trim();
  if (!id) throw new Error('vuln_id is required.');
  const body = {
    note: String(payload?.note || '').trim(),
  };
  if (!body.note) throw new Error('note is required.');
  const scanner = String(payload?.scanner || '').trim();
  if (scanner) body.scanner = scanner;

  const response = await fetchWithAuthRetry(
    toApiUrl(`/dashboard/vulnerabilities/${encodeURIComponent(id)}/notes/`),
    {
      method: 'POST',
      ...jsonFetchOptions(token),
      body: JSON.stringify(body),
    },
    token
  );
  if (!response.ok) {
    if (response.status === 401) {
      await handleUnauthorizedResponse(response);
    }
    const msg = await readApiErrorMessage(response, `Save vulnerability note failed (${response.status})`);
    throw new Error(msg);
  }
  return parseJsonOrThrow(response, 'Vulnerability note');
}

export async function patchDashboardVulnerabilityStatus(vulnId, payload = {}, token = null) {
  const id = String(vulnId || '').trim();
  if (!id) throw new Error('vuln_id is required.');
  const body = {
    status: String(payload?.status || '').trim().toLowerCase(),
  };
  if (!body.status) throw new Error('status is required.');
  const scanner = String(payload?.scanner || '').trim();
  if (scanner) body.scanner = scanner;

  const response = await fetchWithAuthRetry(
    toApiUrl(`/dashboard/vulnerabilities/${encodeURIComponent(id)}/status/`),
    {
      method: 'PATCH',
      ...jsonFetchOptions(token),
      body: JSON.stringify(body),
    },
    token
  );
  if (!response.ok) {
    if (response.status === 401) {
      await handleUnauthorizedResponse(response);
    }
    const msg = await readApiErrorMessage(response, `Update vulnerability status failed (${response.status})`);
    throw new Error(msg);
  }
  return parseJsonOrThrow(response, 'Vulnerability status');
}

export async function deleteDashboardVulnerability(vulnId, payload = {}, token = null) {
  const id = String(vulnId || '').trim();
  if (!id) throw new Error('vuln_id is required.');
  const params = new URLSearchParams();
  const scanner = String(payload?.scanner || '').trim();
  if (scanner) params.set('scanner', scanner);
  const qs = params.toString() ? `?${params.toString()}` : '';

  const response = await fetchWithAuthRetry(
    toApiUrl(`/dashboard/vulnerabilities/${encodeURIComponent(id)}/${qs}`),
    {
      method: 'DELETE',
      ...jsonFetchOptions(token),
    },
    token
  );
  if (!response.ok) {
    if (response.status === 401) {
      await handleUnauthorizedResponse(response);
    }
    const msg = await readApiErrorMessage(response, `Delete vulnerability failed (${response.status})`);
    throw new Error(msg);
  }
  if (response.status === 204) return { ok: true, vuln_id: id };
  try {
    return await parseJsonOrThrow(response, 'Vulnerability delete');
  } catch (_) {
    return { ok: true, vuln_id: id };
  }
}

export async function exportVulnerabilities(query = {}, token = null) {
  const params = new URLSearchParams();
  if (query && typeof query === 'object') {
    Object.entries(query).forEach(([key, value]) => {
      const normalized = String(value ?? '').trim();
      if (normalized) params.set(key, normalized);
    });
  }

  const qs = params.toString() ? `?${params.toString()}` : '';
  const response = await fetchWithAuthRetry(
    toApiUrl(`/dashboard/exports/vulnerabilities/${qs}`),
    {
      method: 'POST',
      ...jsonFetchOptions(token),
      body: JSON.stringify(query || {}),
    },
    token
  );

  if (!response.ok) {
    const msg = await readApiErrorMessage(response, `Export vulnerabilities failed (${response.status})`);
    throw new Error(msg);
  }

  return parseJsonOrThrow(response, 'Export vulnerabilities');
}

export async function getTopRisks(query = null, token = null) {
  const qs =
    query && typeof query === 'object' && Object.keys(query).length > 0
      ? `?${new URLSearchParams(query).toString()}`
      : '';

  const response = await fetchWithAuthRetry(
    toApiUrl(`/ai/top-risks/${qs}`),
    jsonFetchOptions(token),
    token
  );

  if (!response.ok) {
    const msg = await readApiErrorMessage(response, `Top risks fetch failed (${response.status})`);
    throw new Error(msg);
  }

  return parseJsonOrThrow(response, 'AI top risks');
}
